<template>
  <div class="flex flex-col gap-2">
    <div class="text-label text-muted">{{ t('addTask.project') }}</div>
    <div class="flex flex-wrap items-center gap-2">
      <template v-if="orderedProjects.length">
        <SimpleButton
          v-for="chip in chipProjects"
          :key="chip.id"
          size="sm"
          :pressed="chip.id === projectId"
          class="min-w-0 max-w-36 shrink! rounded-chip! pl-1.5! font-bold!"
          :class="chip.id === projectId ? 'cursor-default!' : ''"
          @click="select(chip)"
        >
          <ProjectAvatar :project="chip" class="size-5 rounded-md" />
          <span class="min-w-0 truncate">{{ chip.name }}</span>
        </SimpleButton>

        <Dropdown
          v-if="restProjects.length"
          stickLeft
          dropContentClass="bg-popover-solid! shadow-[var(--shadow-dropdown)]!"
          @click.stop
          @visibilityChanged="dropdownOpen = $event"
        >
          <template #wrapper>
            <SimpleButton
              size="sm"
              :pressed="dropdownOpen"
              class="rounded-chip! font-bold!"
              :text="`+${restProjects.length}`"
            />
          </template>
          <template #items>
            <AppSearchBox
              class="w-80"
              :items="restProjects"
              :getLabel="project => project.name"
              :placeholder="t('addTask.search_projects')"
              @select="select"
            >
              <template #item="{ item }">
                <ProjectAvatar :project="item" class="size-5 rounded-md" />
                <div class="min-w-0 truncate text-left">{{ item.name }}</div>
              </template>
              <template #empty>
                <div class="px-2.5 py-1.5 text-label text-muted">
                  {{ t('addTask.no_projects_found') }}
                </div>
              </template>
            </AppSearchBox>
          </template>
        </Dropdown>
      </template>
      <template v-else>
        <div
          v-for="i in CHIPS_SHOWN"
          :key="`project-skeleton-${i}`"
          class="skeleton h-8 w-24"
        ></div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import Dropdown from '@/components/elements/Dropdown';
import SimpleButton from '@/components/elements/SimpleButton';
import AppSearchBox from '@/components/elements/AppSearchBox';
import ProjectAvatar from '@/components/elements/ProjectAvatar';

const CHIPS_SHOWN = 3;

const props = defineProps({
  // raw store.state.project.projectsList entries
  projects: {
    type: Array,
    required: true,
  },
  projectId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:projectId']);

const { t } = useI18n();

const dropdownOpen = ref(false);

// most-recently-attended first, with the selected project pinned to the front so
// it is always chip[0] — that is also how a pick from the dropdown gets promoted
const orderedProjects = computed(() => {
  const projects = props.projects
    .filter(project => !project.archived)
    .sort((a, b) => {
      const first = a.projectMembers[0].lastActivity; // projectMembers[0] is you
      const second = b.projectMembers[0].lastActivity;
      return dayjs(second) - dayjs(first);
    });

  const selectedIdx = projects.findIndex(project => project.id === props.projectId);
  if (selectedIdx > 0) {
    projects.unshift(...projects.splice(selectedIdx, 1));
  }
  return projects;
});

const chipProjects = computed(() => orderedProjects.value.slice(0, CHIPS_SHOWN));
const restProjects = computed(() => orderedProjects.value.slice(CHIPS_SHOWN));

const select = (project) => {
  if (project.id !== props.projectId) {
    emit('update:projectId', project.id);
  }
};

// the modal can be opened with no project in context (My tasks page), and the
// list itself arrives from the socket after mount — the most recently attended
// project is the selection then, so chip[0] is never left inactive
watch(orderedProjects, (projects) => {
  if (projects.length && !projects.some(project => project.id === props.projectId)) {
    emit('update:projectId', projects[0].id);
  }
}, { immediate: true });
</script>
